import { useParams, useLocation } from 'react-router-dom';
import {
  IconLoader,
  usePrevious,
  PermissionEnum,
  PermissionDenied,
  ButtonPrimary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setMatchSelected } from './store/slices';
import NewApplicationInfoTop from '../common/new-application-info-top';
import { ApplicationType, ChannelStatus } from '../types';
import useQuestionnarie from './hooks/use-questionnarie';
import useApplication from './hooks/use-application';
import ApplicationSteps from './parts/steps';
import ApplicationWrapper from './parts/application-wrapper';

import cn from '../common/styles.module.css';

const NewApplicationCorporateInfo = () => {
  const { validators, applicationChat } = useAppSelector(
    (state) => state.newApplications.list,
  );
  const {
    newApplicationInfo,
    stepsStatus: applicationStepsStatus,
    systemLogs,
    documents,
    agreements,
    agreementsErrorsList,
    mainRepresentativeApplications,
    companyOfficialsApplications,
    ongoingMonitoring,
    ongoingCheckingStatus,
    questionnarie,
    changeLogs,
    changeLogsPagination,
    riskScore,
    amlUpdateStatus,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    busniessActivities,
    customerMainCleints,
    salesChannels,
    customerSourceOfWealth,
    customerSourceOfFunds,
    annualIncomes,
    typeOfAccounts,
    accountsCurrencies,
    questionnarieId,
    accountsFlows,
    expectedFundsOrigins,
    allowedDocumentsCategories,
  } = useAppSelector((state) => state.newApplications.corporateInfo);
  const { infoDictionariesIsLoading, applicationSchemes } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const prevApplicationId = usePrevious(applicationId);

  const {
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    defaultQuestionnarieFormValues,
    questionnarieIsLoading,
    questionnarieIsFetching,
    createAccountsCurrenciesIsProgress,
    createAccountsFlowsIsProgress,
    createExpectedFundsOriginsInProgress,
    updateAccountsCurrenciesInProgress,
    updateAccountsFlowsInProgress,
    updateExpectedFundsOriginsInProgress,
    questionnarieClearErrors,
    totalIncomeError,
    questionnarieControl,
    questionnarieErrors,
    questionnarieSetValue,
    setTotalIncomeError,
    setAccountsCurrenciesValues,
    onDeleteAccountCurrencies,
    selectedAccountsCurrencyId,
    deleteAccountsCurrenciesInProgress,
    setAccountsFlowsValues,
    selectedExpectedFundsOriginsId,
    accountsCurrenciesIsLoading,
    accountsCurrenciesIsFetching,
    accountsFlowsIsLoading,
    accountsFlowsIsFetching,
    expectedFundsOriginsIsLoading,
    expectedFundsOriginsIsFetching,
    deleteExpectedFundsOriginsInProgress,
    onDeleteExpectedFundsOrigins,
    setExpectedFundsOriginsValues,
    questionnarieIsDirty,
  } = useQuestionnarie({
    expectedFundsOrigins,
    questionnarieId,
    accountsCurrencies,
    accountsFlows,
    newApplicationInfo,
    questionnarie,
    activePermissions,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    busniessActivities,
    customerMainCleints,
    salesChannels,
    customerSourceOfWealth,
    customerSourceOfFunds,
    annualIncomes,
    typeOfAccounts,
  });

  const {
    userCreated,
    isLoading,
    isFetching,
    agreementsLoading,
    agreementsFetching,
    applicationLoadErrorVisible,
    getApplicationDocuments,
    setStatusChangeError,
    setSuccessMessageVisible,
    requestInProgress,
    questionnarieUpdateInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    setError,
    clearErrors,
    customersSuppliersStepEnabled,
    control,
    errors,
    setValue,
    initialFields,
    onApplicationCOFSubmit,
    setDefaultFormValues,
    getApplicationChat,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    successMessageVisible,
    link,
    linkRef,
    createInProgress,
    onSubmit,
    onCreateCustomer,
  } = useApplication({
    documents,
    agreements,
    mainRepresentativeApplications,
    companyOfficialsApplications,
    ongoingMonitoring,
    ongoingCheckingStatus,
    applicationStepsStatus,
    allowedDocumentsCategories,
    newApplicationInfo,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    applicationId,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    activePermissions,
    prevApplicationId,
    validators,
    applicationSchemes,
    amlUpdateStatus,
    defaultQuestionnarieFormValues,
  });

  let content = <div />;

  if (!userCreated && (isLoading || isFetching)) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    (!isLoading &&
      !isFetching &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_LIST,
      )) ||
    userCreated
  ) {
    if (applicationLoadErrorVisible) {
      content = (
        <div className={cn.applicationLoadError}>
          <div className={cn.applicationLoadErrorTitle}>
            Something went wrong. Reload page
          </div>
          <ButtonPrimary
            text="Reload"
            className={cn.reloadButton}
            onClick={() => window.location.reload()}
            bold
          />
        </div>
      );
    } else {
      content = (
        <div className={cn.newApplicationInfoContent}>
          <NewApplicationInfoTop
            getApplicationDocuments={getApplicationDocuments}
            setStatusChangeError={setStatusChangeError}
            newApplicationInfo={newApplicationInfo}
            setSuccessMessageVisible={setSuccessMessageVisible}
            stepsStatus={applicationStepsStatus}
            setMatchSelected={(data) => dispatch(setMatchSelected(data))}
            applicationType={ApplicationType.CORPORATE}
            applicationId={applicationId}
            onSubmit={onSubmit}
            onCreate={() => onCreateCustomer()}
            createAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
            )}
            createInProgress={createInProgress}
            requestInProgress={
              requestInProgress ||
              questionnarieUpdateInProgress ||
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress
            }

            fields={fields}
            questionnarieFields={questionnarieFields}
            defaultFormValues={defaultFormValues}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            canSelectResponsible={activePermissions?.includes(
              PermissionEnum.USERS_LIST,
            )}
            statusChangeError={statusChangeError}
            title={
              newApplicationInfo
                ? newApplicationInfo.companyName
                : 'New Application'
            }
            applicationChat={applicationChat?.find(
              (item) => item.status === ChannelStatus.OPEN,
            )}
            riskScore={riskScore}
            setError={setError}
            clearErrors={clearErrors}
            customersSuppliersStepEnabled={customersSuppliersStepEnabled}
            isCorporate
            questionnarieSetError={questionnarieSetError}
            questionnarieClearErrors={questionnarieClearErrors}
            changeLogs={changeLogs}
            changeLogsPagination={changeLogsPagination}
            systemLogs={systemLogs}
            amlUpdateStatus={amlUpdateStatus}
            totalIncomeError={totalIncomeError}
            onSaveAccountsCurrencies={onSaveAccountsCurrencies}
            accountsCurrenciesValues={accountsCurrenciesValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose,
                plannedTransactionNature: item.plannedTransactionNature,
              }))}
            accountsCurrencies={accountsCurrencies?.map((item) => ({
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose,
              plannedTransactionNature: item.plannedTransactionNature,
            }))}
            onSaveAccountsFlows={onSaveAccountsFlows}
            accountsFlowsValues={accountsFlowsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover:
                  item.expectedAnnualCreditTurnover?.toString() || '',
                maxTransferAmountPerTransaction:
                  item.maxTransferAmountPerTransaction?.toString() || '',
                transactionFrequency:
                  item.transactionFrequency?.toString() || '',
                transactionFrequencyPeriodMonths:
                  item.transactionFrequencyPeriodMonths?.toString() || '',
              }))}
            accountsFlows={accountsFlows?.map((item) => ({
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover:
                item.expectedAnnualCreditTurnover?.toString() || '',
              maxTransferAmountPerTransaction:
                item.maxTransferAmountPerTransaction?.toString() || '',
              transactionFrequency: item.transactionFrequency?.toString() || '',
              transactionFrequencyPeriodMonths:
                item.transactionFrequencyPeriodMonths?.toString() || '',
            }))}
            onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            expectedFundsOriginsValues={expectedFundsOriginsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item?.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item?.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item?.counterpartyBankCountryIds || [],
                activityCountryIds: item?.activityCountryIds || [],
                mainActivities: item?.mainActivities || '',
                transactionReason: item?.transactionReason || '',
                transactionType: item?.transactionType || '',
              }))}
            expectedFundsOrigins={expectedFundsOrigins?.map((item) => ({
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item?.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item?.countryOfIncorporationId || '',
              counterpartyBankCountryIds:
                item?.counterpartyBankCountryIds || [],
              activityCountryIds: item?.activityCountryIds || [],
              mainActivities: item?.mainActivities || '',
              transactionReason: item?.transactionReason || '',
              transactionType: item?.transactionType || '',
            }))}
            stepOneBlockLoadingInProgress={
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress
            }
            infoDictionariesIsLoading={
              infoDictionariesIsLoading ||
              questionnarieIsLoading ||
              (!questionnarie && questionnarieIsFetching)
            }
          />
          <ApplicationSteps
            applicationStepsStatus={applicationStepsStatus}
            control={control}
            errors={errors}
            fields={fields}
            newApplicationInfo={newApplicationInfo}
            setValue={setValue}
            activePermissions={activePermissions}
            clearErrors={clearErrors}
            questionnarieControl={questionnarieControl}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieErrors={questionnarieErrors}
            questionnarieFields={questionnarieFields}
            relationshipToTheCustomerDictionary={
              relationshipToTheCustomerDictionary
            }
            sourceOfWealth={sourceOfWealth}
            busniessActivities={busniessActivities}
            customerMainCleints={customerMainCleints}
            salesChannels={salesChannels}
            questionnarieSetValue={questionnarieSetValue}
            customerSourceOfWealth={customerSourceOfWealth}
            customerSourceOfFunds={customerSourceOfFunds}
            annualIncomes={annualIncomes}
            totalIncomeError={totalIncomeError}
            setTotalIncomeError={setTotalIncomeError}
            typeOfAccounts={typeOfAccounts}
            accountsCurrenciesValues={accountsCurrenciesValues}
            setAccountsCurrenciesValues={setAccountsCurrenciesValues}
            onDeleteAccountCurrencies={onDeleteAccountCurrencies}
            selectedAccountsCurrencyId={selectedAccountsCurrencyId}
            deleteAccountsCurrenciesInProgress={
              deleteAccountsCurrenciesInProgress
            }
            accountsFlowsValues={accountsFlowsValues}
            setAccountsFlowsValues={setAccountsFlowsValues}
            selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
            accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
            accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
            accountsFlowsIsLoading={accountsFlowsIsLoading}
            accountsFlowsIsFetching={accountsFlowsIsFetching}
            expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
            expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
            deleteExpectedFundsOriginsInProgress={
              deleteExpectedFundsOriginsInProgress
            }
            onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
            expectedFundsOriginsValues={expectedFundsOriginsValues}
            setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
            customersSuppliersStepEnabled={customersSuppliersStepEnabled}
            mainRepresentativeApplications={mainRepresentativeApplications}
            applicationId={applicationId}
            initialFields={initialFields}
            onApplicationCOFSubmit={onApplicationCOFSubmit}
            setDefaultFormValues={setDefaultFormValues}
            defaultFormValues={defaultFormValues}
            companyOfficialsApplications={companyOfficialsApplications}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            getApplicationChat={getApplicationChat}
            onSaveAccountsCurrencies={onSaveAccountsCurrencies}
            onSaveAccountsFlows={onSaveAccountsFlows}
            onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            ongoingMonitoring={ongoingMonitoring}
            ongoingCheckingStatus={ongoingCheckingStatus}
            documents={documents}
            search={search}
            allowedDocumentsCategories={allowedDocumentsCategories}
            agreements={agreements}
            agreementsIsLoading={agreementsLoading || agreementsFetching}
            infoDictionariesIsLoading={infoDictionariesIsLoading}
            questionnarieIsLoading={
              questionnarieIsLoading ||
              (!questionnarie && questionnarieIsFetching)
            }
            agreementsErrorsList={agreementsErrorsList}
          />
        </div>
      );
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!userCreated) {
      content = <PermissionDenied />;
    }
  }

  return (
    <ApplicationWrapper
      isDirty={isDirty}
      questionnarieIsDirty={questionnarieIsDirty}
      setDirtyPopupVisible={setDirtyPopupVisible}
      dirtyPopupVisible={dirtyPopupVisible}
      successMessageVisible={successMessageVisible}
      link={link}
      linkRef={linkRef}
    >
      {content}
    </ApplicationWrapper>
  );
};

export default NewApplicationCorporateInfo;
